import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, Eye, EyeSlash, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "mt-1.5 border-[#E2E5EA] bg-[#FFFFFF] text-[#0F172A] focus-visible:ring-red-500";
const labelCls = "text-xs uppercase tracking-wider text-[#9CA3AF]";

const PasswordField = ({ id, value, onChange, label, testId, show, setShow }) => (
  <div>
    <Label htmlFor={id} className={labelCls}>{label}</Label>
    <div className="relative mt-1.5">
      <Input id={id} type={show ? "text" : "password"} data-testid={testId} value={value}
        onChange={onChange} required
        className="border-[#E2E5EA] bg-[#FFFFFF] pr-10 text-[#0F172A] focus-visible:ring-red-500" />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#0F172A]">
        {show ? <EyeSlash size={18} /> : <Eye size={18} />}
      </button>
    </div>
  </div>
);

export default function Login() {
  const { user, login, register, verifyEmail, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup | forgot
  const [forgotStep, setForgotStep] = useState(1);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const reset = (m) => {
    setMode(m); setError(""); setForgotStep(1);
    setPassword(""); setNewPassword(""); setName("");
  };

  const doSignIn = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await login(email, password); navigate("/"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const doSignUp = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await register(name, email, password); toast.success("Account created — welcome!"); navigate("/"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const doVerifyEmail = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { const d = await verifyEmail(email); toast.success(`Account found — reset password for ${d.name || email}`); setForgotStep(2); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await resetPassword(email, newPassword);
      toast.success("Password updated — please sign in");
      reset("signin"); setPassword("");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-[#F4F5F7] lg:grid-cols-2">
      <div className="relative hidden overflow-hidden border-r border-[#E2E5EA] lg:block">
        <img
          src="https://images.unsplash.com/photo-1638864616275-9f0b291a2eb6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4OTM2MjYzM3ww&ixlib=rb-4.1.0&q=85"
          alt="Abstract geometric" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#F4F5F7] via-transparent to-transparent" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-red-500">
              <Lightning weight="fill" size={20} className="text-white" />
            </div>
            <span className="font-heading text-lg font-extrabold tracking-tight text-[#0F172A]">PayFlow HR</span>
          </div>
          <div>
            <h2 className="font-heading max-w-md text-4xl font-bold leading-tight tracking-tighter text-[#0F172A]">
              The payroll command centre for Malaysian HR teams.
            </h2>
            <p className="mt-4 max-w-sm text-sm text-[#9CA3AF]">
              EPF, SOCSO, EIS &amp; PCB computed automatically. From employee data to payslips and EA forms — all in one place.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-red-500">
              <Lightning weight="fill" size={20} className="text-white" />
            </div>
            <span className="font-heading text-lg font-extrabold tracking-tight text-[#0F172A]">PayFlow HR</span>
          </div>

          {error && (
            <div data-testid="auth-error" className="mb-5 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {error}
            </div>
          )}

          {/* SIGN IN */}
          {mode === "signin" && (
            <form onSubmit={doSignIn} data-testid="login-form">
              <h1 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A]">Welcome back</h1>
              <p className="mt-1 text-sm text-[#9CA3AF]">Sign in to your HR administrator account.</p>
              <div className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="email" className={labelCls}>Email</Label>
                  <Input id="email" type="email" data-testid="login-email" value={email}
                    onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
                </div>
                <PasswordField id="password" value={password} onChange={(e) => setPassword(e.target.value)} label="Password" testId="login-password" show={show} setShow={setShow} />
                <div className="flex justify-end">
                  <button type="button" data-testid="forgot-link" onClick={() => reset("forgot")} className="text-xs text-red-500 hover:text-red-400">
                    Forgot password?
                  </button>
                </div>
              </div>
              <Button type="submit" data-testid="login-submit" disabled={loading} className="mt-6 w-full bg-red-500 font-semibold text-white transition-colors duration-200 hover:bg-red-600">
                {loading ? "Signing in…" : "Sign In"}
              </Button>
              <p className="mt-5 text-center text-sm text-[#9CA3AF]">
                First time here?{" "}
                <button type="button" data-testid="show-signup" onClick={() => reset("signup")} className="font-medium text-red-500 hover:text-red-400">
                  Register as administrator
                </button>
              </p>
            </form>
          )}

          {/* SIGN UP */}
          {mode === "signup" && (
            <form onSubmit={doSignUp} data-testid="signup-form">
              <h1 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A]">Create admin account</h1>
              <p className="mt-1 text-sm text-[#9CA3AF]">Register a new HR administrator to manage payroll.</p>
              <div className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="name" className={labelCls}>Full Name</Label>
                  <Input id="name" data-testid="signup-name" value={name} onChange={(e) => setName(e.target.value)} required className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="su-email" className={labelCls}>Email</Label>
                  <Input id="su-email" type="email" data-testid="signup-email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
                </div>
                <PasswordField id="su-password" value={password} onChange={(e) => setPassword(e.target.value)} label="Password (min 8 chars)" testId="signup-password" show={show} setShow={setShow} />
              </div>
              <Button type="submit" data-testid="signup-submit" disabled={loading} className="mt-6 w-full bg-red-500 font-semibold text-white transition-colors duration-200 hover:bg-red-600">
                {loading ? "Creating…" : "Create Account"}
              </Button>
              <button type="button" data-testid="back-to-signin" onClick={() => reset("signin")} className="mt-5 flex w-full items-center justify-center gap-1.5 text-sm text-[#9CA3AF] hover:text-[#0F172A]">
                <ArrowLeft size={15} /> Back to sign in
              </button>
            </form>
          )}

          {/* FORGOT PASSWORD */}
          {mode === "forgot" && (
            <div data-testid="forgot-form">
              <h1 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A]">Reset password</h1>
              <p className="mt-1 text-sm text-[#9CA3AF]">
                {forgotStep === 1 ? "Enter your account email to continue." : "Set a new password for your account."}
              </p>

              {forgotStep === 1 ? (
                <form onSubmit={doVerifyEmail} className="mt-6 space-y-4">
                  <div>
                    <Label htmlFor="fp-email" className={labelCls}>Email</Label>
                    <Input id="fp-email" type="email" data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
                  </div>
                  <Button type="submit" data-testid="verify-email-submit" disabled={loading} className="w-full bg-red-500 font-semibold text-white hover:bg-red-600">
                    {loading ? "Checking…" : "Continue"}
                  </Button>
                </form>
              ) : (
                <form onSubmit={doReset} className="mt-6 space-y-4">
                  <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-400">
                    <CheckCircle size={16} weight="fill" /> {email}
                  </div>
                  <PasswordField id="fp-new" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} label="New Password (min 8 chars)" testId="reset-password" show={show} setShow={setShow} />
                  <Button type="submit" data-testid="reset-submit" disabled={loading} className="w-full bg-red-500 font-semibold text-white hover:bg-red-600">
                    {loading ? "Updating…" : "Update Password"}
                  </Button>
                </form>
              )}

              <button type="button" data-testid="forgot-back" onClick={() => reset("signin")} className="mt-5 flex w-full items-center justify-center gap-1.5 text-sm text-[#9CA3AF] hover:text-[#0F172A]">
                <ArrowLeft size={15} /> Back to sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
